import { useState, type ReactNode } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { ChevronDownIcon, ChevronUpIcon } from '@shipt/design-system-icons'
import { ActiveFiltersCircle } from '@/features/shared/components/ActiveFiltersCircle'
import { HDivider } from '@/features/shared/components/Dividers'
import { screenSizes } from '@shipt/design-system-themes'
import { Body } from '@shipt/design-system-typography'
import { Box } from '@shipt/design-system-layouts'

type Props = {
  title: string
  isCollapsible?: boolean
  hideDivider?: boolean
  autoExpand?: boolean
  activeFiltersLength?: number
  children: ReactNode
}

export const FilterSection = ({
  title = '',
  children,
  isCollapsible = false,
  hideDivider = false,
  autoExpand = false,
  activeFiltersLength = 0,
}: Props) => {
  const { screenSm } = useMediaQuery()

  const defaultState = !screenSm || !isCollapsible || autoExpand
  const [showFilters, setShowFilters] = useState(defaultState)

  const handleToggleContent = () => {
    if (isCollapsible) {
      setShowFilters(!showFilters)
    }
  }
  const Icon = showFilters ? ChevronUpIcon : ChevronDownIcon
  const showDivider = !hideDivider && screenSm
  const showActiveFiltersCircle = isCollapsible && activeFiltersLength > 0

  return (
    <>
      <TitleWrapperDiv showFilters={showFilters} onClick={handleToggleContent}>
        <FilterSectionH2 as="h2">{title}</FilterSectionH2>
        <IconsWrapper>
          {!!showActiveFiltersCircle && (
            <FilterSectionActiveFiltersCircle number={activeFiltersLength} />
          )}
          {!!isCollapsible && <Icon />}
        </IconsWrapper>
      </TitleWrapperDiv>
      {!!showFilters && (
        <Box marginBottom={{ base: 'sm', md: 'lg' }} padding={{ base: [0, 'lg'], md: [0] }}>
          {children}
        </Box>
      )}
      {!!showDivider && <Divider />}
    </>
  )
}

const TitleWrapperDiv = styled.div<{ showFilters: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 3rem;
  padding: 8px 16px;
  background-color: ${({ showFilters, theme }) => (showFilters ? theme.plum100 : 'transparent')};

  @media ${screenSizes.tablet} {
    padding: 0;
    height: 1.5rem;
    margin-bottom: 0.5rem;
    background-color: transparent;
  }

  svg {
    cursor: pointer;
    color: ${({ theme }) => theme.gray600};
    margin-right: 0;
  }
`

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const FilterSectionH2 = styled(Body)`
  margin: 0;
  color: ${({ theme }) => theme.gray900};

  @media ${screenSizes.tablet} {
    line-height: 1.25rem;
    font-size: 0.9375rem;
  }
`

const FilterSectionActiveFiltersCircle = styled(ActiveFiltersCircle)`
  height: 1.4375rem;
  width: 1.4375rem;
  margin-right: 1.125rem;

  @media ${screenSizes.tablet} {
    margin-right: 0.5rem;
  }
`

const Divider = styled(HDivider)`
  width: auto;
  margin: -0.0625rem 1rem;
`
